import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { expenseTypesService, expensesService, userService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { 
  Plus, Edit, Trash2, Save, X, Calendar, 
  CheckCircle, Tag, CreditCard, AlertCircle, Eye, Check,
  RefreshCw, Clock, RotateCcw, Zap, ArrowDownCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmModal from './ConfirmModal';

const ExpensesManagement = () => {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('expenses'); // 'types' ou 'expenses'
  
  // Estados para tipos de despesas
  const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [typeFormData, setTypeFormData] = useState({ name: '', description: '' });
  
  // Estados para despesas
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [viewingExpenseId, setViewingExpenseId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [expenseFormData, setExpenseFormData] = useState({
    user_id: '',
    expense_type_id: '',
    description: '',
    amount: '',
    payment_method: 'SINGLE',
    installments_count: 1,
    start_month: new Date().getMonth() + 1,
    start_year: new Date().getFullYear(),
    notes: ''
  });

  const [confirmApprove, setConfirmApprove] = useState({ show: false, expenseId: null });
  const [confirmDeleteExpense, setConfirmDeleteExpense] = useState({ show: false, expenseId: null });
  const [confirmDeleteType, setConfirmDeleteType] = useState({ show: false, typeId: null });
  const [confirmMarkPaid, setConfirmMarkPaid] = useState({ show: false, expenseId: null, installmentId: null, installmentNumber: null, totalInstallments: null });

  // Estados para Quitação Manual Antecipada / Forçar Abatimento
  const [settleModalData, setSettleModalData] = useState(null); // { expense, installments: [] }
  const [selectedInstallmentIds, setSelectedInstallmentIds] = useState([]);
  const [settlePaymentMethod, setSettlePaymentMethod] = useState('PIX');
  const [settlePaymentDate, setSettlePaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [settleRegisterCashbox, setSettleRegisterCashbox] = useState(true);
  const [settleNotes, setSettleNotes] = useState('');

  // Buscar funcionários ativos
  const { data: usersData } = useQuery({
    queryKey: ['users-all'],
    queryFn: () => userService.getUsers({ limit: 100, active: true }),
    staleTime: 5 * 60 * 1000,
  });

  const users = useMemo(() => {
    if (!usersData) return [];
    if (Array.isArray(usersData?.data?.users)) return usersData.data.users;
    if (Array.isArray(usersData?.data?.data?.users)) return usersData.data.data.users;
    return [];
  }, [usersData]);

  // Buscar tipos de despesas
  const { data: typesData, isLoading: loadingTypes } = useQuery({
    queryKey: ['expense-types'],
    queryFn: () => expenseTypesService.getExpenseTypes({ active: true }),
    staleTime: 5 * 60 * 1000,
  });

  const expenseTypes = useMemo(() => {
    if (!typesData?.data?.data) return [];
    return Array.isArray(typesData.data.data) ? typesData.data.data : [];
  }, [typesData]);

  // Buscar despesas
  const { data: expensesData, isLoading: loadingExpenses } = useQuery({
    queryKey: ['expenses', selectedUserId, selectedStatus, selectedMonth, selectedYear],
    queryFn: () => expensesService.getExpenses({
      userId: selectedUserId || undefined,
      status: selectedStatus || undefined,
      month: selectedMonth || undefined,
      year: selectedYear || undefined,
    }),
    staleTime: 0,
  });

  const expenses = useMemo(() => {
    if (!expensesData?.data?.data) return [];
    return Array.isArray(expensesData.data.data) ? expensesData.data.data : [];
  }, [expensesData]);

  const summary = useMemo(() => {
    return expensesData?.data?.summary || {
      total_open_amount: 0,
      current_month_due_amount: 0,
      pending_approval_count: 0,
      total_paid_year_amount: 0
    };
  }, [expensesData]);

  const hasActiveFilters = Boolean(selectedUserId || selectedMonth || selectedYear || selectedStatus);

  const clearFilters = () => {
    setSelectedUserId('');
    setSelectedMonth('');
    setSelectedYear('');
    setSelectedStatus('');
  };

  // Buscar detalhes da despesa selecionada para visualização
  const { data: expenseDetailsData } = useQuery({
    queryKey: ['expense-details', viewingExpenseId],
    queryFn: () => expensesService.getExpense(viewingExpenseId),
    enabled: !!viewingExpenseId,
    staleTime: 0,
  });

  const expenseDetails = expenseDetailsData?.data?.data || null;

  // Mutations para tipos
  const createTypeMutation = useMutation({
    mutationFn: (data) => expenseTypesService.createExpenseType(data),
    onSuccess: () => {
      toast.success('Tipo de despesa criado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['expense-types'] });
      setIsTypeModalOpen(false);
      setTypeFormData({ name: '', description: '' });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao criar tipo de despesa');
    },
  });

  const updateTypeMutation = useMutation({
    mutationFn: ({ id, data }) => expenseTypesService.updateExpenseType(id, data),
    onSuccess: () => {
      toast.success('Tipo de despesa atualizado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['expense-types'] });
      setIsTypeModalOpen(false);
      setEditingType(null);
      setTypeFormData({ name: '', description: '' });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar tipo de despesa');
    },
  });

  const deleteTypeMutation = useMutation({
    mutationFn: (id) => expenseTypesService.deleteExpenseType(id),
    onSuccess: () => {
      toast.success('Tipo de despesa removido com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['expense-types'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao remover tipo de despesa');
    },
  });

  // Mutations para despesas
  const createExpenseMutation = useMutation({
    mutationFn: (data) => expensesService.createExpense(data),
    onSuccess: () => {
      toast.success('Vale criado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      setIsExpenseModalOpen(false);
      resetExpenseForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao criar despesa');
    },
  });

  const updateExpenseMutation = useMutation({
    mutationFn: ({ id, data }) => expensesService.updateExpense(id, data),
    onSuccess: () => {
      toast.success('Vale atualizado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      setIsExpenseModalOpen(false);
      setEditingExpense(null);
      resetExpenseForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar despesa');
    },
  });

  const deleteExpenseMutation = useMutation({
    mutationFn: (id) => expensesService.deleteExpense(id),
    onSuccess: () => {
      toast.success('Vale removido com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao remover despesa');
    },
  });

  const approveExpenseMutation = useMutation({
    mutationFn: (id) => expensesService.approveExpense(id),
    onSuccess: () => {
      toast.success('Aprovação registrada com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao aprovar despesa');
    },
  });

  const markInstallmentPaidMutation = useMutation({
    mutationFn: ({ expenseId, installmentId }) => 
      expensesService.markInstallmentPaid(expenseId, installmentId),
    onSuccess: () => {
      toast.success('Parcela marcada como paga!');
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expense-details', viewingExpenseId] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao marcar parcela como paga');
    },
  });

  const recalculateInstallmentsMutation = useMutation({
    mutationFn: (id) => expensesService.recalculateInstallments(id),
    onSuccess: () => {
      toast.success('Parcelas recalculadas com valor exato!');
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expense-details', viewingExpenseId] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao recalcular parcelas');
    },
  });

  const settleExpenseManualMutation = useMutation({
    mutationFn: ({ id, data }) => expensesService.settleExpenseManual(id, data),
    onSuccess: (response) => {
      toast.success(response?.data?.message || 'Quitação manual realizada com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expense-details', viewingExpenseId] });
      queryClient.invalidateQueries({ queryKey: ['cashbox'] });
      queryClient.invalidateQueries({ queryKey: ['cashbox-balance'] });
      setSettleModalData(null);
      setSelectedInstallmentIds([]);
      setSettleNotes('');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao processar quitação manual');
    },
  });

  const openSettleModal = async (expense) => {
    try {
      let insts = [];
      if (expense.installments && Array.isArray(expense.installments)) {
        insts = expense.installments;
      } else {
        const res = await expensesService.getExpense(expense.id);
        insts = res?.data?.data?.installments || res?.data?.installments || [];
      }
      const pending = insts.filter(i => i.status !== 'PAID');
      setSettleModalData({ expense, installments: insts });
      // Se houver pendentes, pré-marca as pendentes. Se todas já constavam como pagas, pré-marca todas para facilitar a injeção no caixa
      setSelectedInstallmentIds(pending.length > 0 ? pending.map(i => i.id) : insts.map(i => i.id));
      setSettlePaymentMethod('PIX');
      setSettlePaymentDate(new Date().toISOString().split('T')[0]);
      setSettleRegisterCashbox(true);
      setSettleNotes('');
    } catch (err) {
      toast.error('Erro ao carregar parcelas para quitação manual');
    }
  };

  const resetExpenseForm = () => {
    setExpenseFormData({
      user_id: '',
      expense_type_id: '',
      description: '',
      amount: '',
      payment_method: 'SINGLE',
      installments_count: 1,
      start_month: new Date().getMonth() + 1,
      start_year: new Date().getFullYear(),
      notes: ''
    });
    setEditingExpense(null);
  };

  const handleTypeSubmit = (e) => {
    e.preventDefault();
    if (editingType) {
      updateTypeMutation.mutate({ id: editingType.id, data: typeFormData });
    } else {
      createTypeMutation.mutate(typeFormData);
    }
  };

  const handleExpenseSubmit = (e) => {
    e.preventDefault();
    if (editingExpense) {
      updateExpenseMutation.mutate({ 
        id: editingExpense.id, 
        data: {
          description: expenseFormData.description,
          notes: expenseFormData.notes
        } 
      });
    } else {
      const data = {
        ...expenseFormData,
        user_id: parseInt(expenseFormData.user_id),
        expense_type_id: parseInt(expenseFormData.expense_type_id),
        amount: parseFloat(expenseFormData.amount),
        installments_count: expenseFormData.payment_method === 'SINGLE' ? 1 : parseInt(expenseFormData.installments_count),
        start_month: parseInt(expenseFormData.start_month),
        start_year: parseInt(expenseFormData.start_year),
      };
      createExpenseMutation.mutate(data);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const getStatusBadge = (status) => {
    const badges = {
      PENDING: { color: 'bg-yellow-100 text-yellow-800', label: 'Pendente Pagamento' },
      PENDING_APPROVAL: { color: 'bg-orange-100 text-orange-800', label: 'Pendente Aprovação' },
      APPROVED: { color: 'bg-blue-100 text-blue-800', label: 'Aprovado' },
      PAID: { color: 'bg-green-100 text-green-800', label: 'Pago' },
      CANCELLED: { color: 'bg-red-100 text-red-800', label: 'Cancelado' },
    };
    const badge = badges[status] || badges.PENDING;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  return (
    <div className="p-3 sm:p-4 lg:p-6 space-y-4">
      <div>
        <h1 className="text-lg sm:text-xl font-bold text-gray-900">Vales / Empréstimos</h1>
        <p className="text-xs sm:text-sm text-gray-600">Gerencie vales e empréstimos para funcionários</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('expenses')}
              className={`px-4 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-medium border-b-2 ${
                activeTab === 'expenses'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <CreditCard className="h-4 w-4 inline mr-1.5 sm:mr-2" />
              <span className="hidden sm:inline">Vales / Empréstimos</span>
              <span className="sm:hidden">Vales</span>
            </button>
            <button
              onClick={() => setActiveTab('types')}
              className={`px-4 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-medium border-b-2 ${
                activeTab === 'types'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Tag className="h-4 w-4 inline mr-1.5 sm:mr-2" />
              <span className="hidden sm:inline">Tipos de Vales</span>
              <span className="sm:hidden">Tipos</span>
            </button>
          </nav>
        </div>
      </div>

      {/* Conteúdo da aba Vales */}
      {activeTab === 'expenses' && (
        <>
          {/* Cards de Resumo Gerencial (KPIs) */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <div className="bg-white rounded-xl border border-amber-200 p-3 sm:p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-[11px] sm:text-xs font-semibold uppercase tracking-wider text-amber-800">Total em Aberto</span>
                <div className="p-2 rounded-lg bg-amber-100 text-amber-700">
                  <CreditCard className="w-4 h-4" />
                </div>
              </div>
              <p className="text-base sm:text-xl font-bold text-gray-900 mt-2 font-mono">
                {formatCurrency(summary.total_open_amount)}
              </p>
              <p className="text-[10px] sm:text-[11px] text-gray-500 mt-0.5">Saldo devedor total ativo</p>
            </div>

            <div className="bg-white rounded-xl border border-emerald-200 p-3 sm:p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-[11px] sm:text-xs font-semibold uppercase tracking-wider text-emerald-800">A Descontar no Mês</span>
                <div className="p-2 rounded-lg bg-emerald-100 text-emerald-700">
                  <Calendar className="w-4 h-4" />
                </div>
              </div>
              <p className="text-base sm:text-xl font-bold text-emerald-700 mt-2 font-mono">
                {formatCurrency(summary.current_month_due_amount)}
              </p>
              <p className="text-[10px] sm:text-[11px] text-gray-500 mt-0.5">Vencendo na folha atual</p>
            </div>

            <div className="bg-white rounded-xl border border-orange-200 p-3 sm:p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-[11px] sm:text-xs font-semibold uppercase tracking-wider text-orange-800">Aguardando Aprovação</span>
                <div className="p-2 rounded-lg bg-orange-100 text-orange-700">
                  <Clock className="w-4 h-4" />
                </div>
              </div>
              <p className="text-base sm:text-xl font-bold text-orange-700 mt-2 font-mono">
                {summary.pending_approval_count}
              </p>
              <p className="text-[10px] sm:text-[11px] text-gray-500 mt-0.5">Aguardando assinatura</p>
            </div>

            <div className="bg-white rounded-xl border border-blue-200 p-3 sm:p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-[11px] sm:text-xs font-semibold uppercase tracking-wider text-blue-800">Quitados no Ano</span>
                <div className="p-2 rounded-lg bg-blue-100 text-blue-700">
                  <CheckCircle className="w-4 h-4" />
                </div>
              </div>
              <p className="text-base sm:text-xl font-bold text-blue-700 mt-2 font-mono">
                {formatCurrency(summary.total_paid_year_amount)}
              </p>
              <p className="text-[10px] sm:text-[11px] text-gray-500 mt-0.5">Amortizado em {new Date().getFullYear()}</p>
            </div>
          </div>

          {/* Filtros */}
          <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Funcionário</label>
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Todos</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.full_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Mês Vencimento</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Todos</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={m}>
                      {new Date(2000, m - 1, 1).toLocaleString('pt-BR', { month: 'short' })}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Ano Vencimento</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Todos</option>
                  {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i).map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Todos</option>
                  <option value="PENDING_APPROVAL">Pendente Aprovação</option>
                  <option value="APPROVED">Aprovado</option>
                  <option value="PENDING">Pendente Pagamento</option>
                  <option value="PAID">Pago</option>
                  <option value="CANCELLED">Cancelado</option>
                </select>
              </div>
              <div className="flex items-end">
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="w-full px-3 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg flex items-center justify-center gap-1 transition-colors"
                    title="Limpar todos os filtros"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    <span>Limpar</span>
                  </button>
                )}
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => {
                    resetExpenseForm();
                    setIsExpenseModalOpen(true);
                  }}
                  className="w-full bg-primary-600 text-white px-3 py-1.5 text-sm rounded-lg hover:bg-primary-700 flex items-center justify-center gap-1.5 shadow-sm"
                >
                  <Plus className="h-4 w-4" />
                  <span>Novo Vale</span>
                </button>
              </div>
            </div>
          </div>

          {/* Tabela de Despesas */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {loadingExpenses ? (
              <div className="p-6 text-center text-gray-500 text-sm">Carregando...</div>
            ) : expenses.length === 0 ? (
              <div className="p-6 text-center text-gray-500 text-sm">Nenhum vale / empréstimo encontrado</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Ações</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Valor</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Modalidade & Quitação</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Funcionário</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {expenses.map((expense) => {
                      const totalInstallments = expense.installments_count || 1;
                      const paidInstallments = expense.installments_paid || 0;
                      const progressPct = Math.min(100, Math.round((paidInstallments / totalInstallments) * 100));

                      return (
                      <tr key={expense.id} id={`expense-${expense.id}`} className="hover:bg-gray-50 transition-colors duration-500 text-xs">
                        <td className="px-3 py-2 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => setViewingExpenseId(expense.id)}
                              className="p-1 text-blue-600 hover:text-blue-900"
                              title="Visualizar Detalhes"
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </button>

                            {/* Botão de Quitação Manual / Forçar Abatimento */}
                            {expense.status !== 'CANCELLED' && expense.status !== 'PAID' && (
                              <button
                                onClick={() => openSettleModal(expense)}
                                className="p-1 text-emerald-600 hover:text-emerald-900 hover:bg-emerald-50 rounded"
                                title="Quitação Manual / Forçar Abatimento no Caixa"
                              >
                                <Zap className="h-3.5 w-3.5" />
                              </button>
                            )}

                            {expense.status === 'PENDING_APPROVAL' &&
                             currentUser?.role === 'GERENCIA' &&
                             !expense.approvals_list?.some(a => a.id === currentUser.id) && (
                              <button
                                onClick={() => {
                                  setConfirmApprove({ show: true, expenseId: expense.id });
                                }}
                                className="p-1 text-green-600 hover:text-green-900"
                                title="Aprovar"
                              >
                                <Check className="h-3.5 w-3.5" />
                              </button>
                            )}

                            <button
                              onClick={() => {
                                setEditingExpense(expense);
                                setExpenseFormData({
                                  user_id: expense.user_id.toString(),
                                  expense_type_id: expense.expense_type_id.toString(),
                                  description: expense.description || '',
                                  amount: expense.amount.toString(),
                                  payment_method: expense.payment_method,
                                  installments_count: expense.installments_count.toString(),
                                  start_month: expense.start_month,
                                  start_year: expense.start_year,
                                  notes: expense.notes || ''
                                });
                                setIsExpenseModalOpen(true);
                              }}
                              className="p-1 text-primary-600 hover:text-primary-900"
                              title="Editar"
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => {
                                setConfirmDeleteExpense({ show: true, expenseId: expense.id });
                              }}
                              className="p-1 text-red-600 hover:text-red-900"
                              title="Excluir"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-center">
                          {getStatusBadge(expense.status)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-right font-medium text-gray-900">{formatCurrency(expense.amount)}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-center">
                          {expense.payment_method === 'SINGLE' ? (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold ${
                              paidInstallments >= 1 ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-blue-50 text-blue-800 border border-blue-200'
                            }`}>
                              {paidInstallments >= 1 ? 'À vista (Quitado)' : 'À vista (Aguardando Folha)'}
                            </span>
                          ) : (
                            <div className="flex flex-col items-center gap-1 min-w-[120px] max-w-[150px] mx-auto" title={`${paidInstallments} pagas de ${totalInstallments} (Restam ${totalInstallments - paidInstallments})`}>
                              <div className="flex items-center justify-between w-full text-[11px] font-semibold text-gray-700">
                                <span>{paidInstallments} de {totalInstallments} pagas</span>
                                <span className="text-gray-500 font-mono text-[10px]">{progressPct}%</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                                <div
                                  className={`h-full transition-all duration-300 ${
                                    paidInstallments === totalInstallments ? 'bg-emerald-500' : 'bg-primary-500'
                                  }`}
                                  style={{ width: `${progressPct}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className="font-medium text-gray-900">{expense.user_name}</span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-900">{expense.expense_type_name}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Conteúdo da aba Tipos */}
      {activeTab === 'types' && (
        <>
          <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4">
            <button
              onClick={() => {
                setEditingType(null);
                setTypeFormData({ name: '', description: '' });
                setIsTypeModalOpen(true);
              }}
              className="bg-primary-600 text-white px-3 py-1.5 text-sm rounded-lg hover:bg-primary-700 flex items-center gap-1.5"
            >
              <Plus className="h-4 w-4" />
              Novo Tipo
            </button>
          </div>

          {/* Lista de Tipos */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {loadingTypes ? (
              <div className="p-6 text-center text-gray-500 text-sm">Carregando...</div>
            ) : expenseTypes.length === 0 ? (
              <div className="p-6 text-center text-gray-500 text-sm">Nenhum tipo de despesa encontrado</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {expenseTypes.map((type) => (
                      <tr key={type.id} className="hover:bg-gray-50 text-xs">
                        <td className="px-3 py-2 whitespace-nowrap font-medium text-gray-900">{type.name}</td>
                        <td className="px-3 py-2 text-gray-500">{type.description || '-'}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => {
                                setEditingType(type);
                                setTypeFormData({
                                  name: type.name,
                                  description: type.description || ''
                                });
                                setIsTypeModalOpen(true);
                              }}
                              className="p-1 text-primary-600 hover:text-primary-900"
                              title="Editar"
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => {
                                setConfirmDeleteType({ show: true, typeId: type.id });
                              }}
                              className="p-1 text-red-600 hover:text-red-900"
                              title="Excluir"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Modal de Tipo de Despesa */}
      {isTypeModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                {editingType ? 'Editar Tipo de Despesa' : 'Novo Tipo de Despesa'}
              </h2>
              <button onClick={() => setIsTypeModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleTypeSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                <input
                  type="text"
                  value={typeFormData.name}
                  onChange={(e) => setTypeFormData({ ...typeFormData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                <textarea
                  value={typeFormData.description}
                  onChange={(e) => setTypeFormData({ ...typeFormData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows="3"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsTypeModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  Salvar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Despesa */}
      {isExpenseModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                {editingExpense ? 'Editar Vale' : 'Novo Vale'}
              </h2>
              <button onClick={() => setIsExpenseModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleExpenseSubmit} className="p-6 space-y-4">
              {editingExpense && (
                <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg text-xs text-amber-900 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <strong>Segurança Contábil:</strong> Funcionário, valor, parcelamento e período não podem ser alterados após a contratação do vale. Caso precise alterar esses valores, cancele este vale e crie um novo.
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Funcionário *</label>
                  <select
                    value={expenseFormData.user_id}
                    onChange={(e) => setExpenseFormData({ ...expenseFormData, user_id: e.target.value })}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${editingExpense ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                    disabled={!!editingExpense}
                    required
                  >
                    <option value="">Selecione...</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.full_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Despesa *</label>
                  <select
                    value={expenseFormData.expense_type_id}
                    onChange={(e) => setExpenseFormData({ ...expenseFormData, expense_type_id: e.target.value })}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${editingExpense ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                    disabled={!!editingExpense}
                    required
                  >
                    <option value="">Selecione...</option>
                    {expenseTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valor *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={expenseFormData.amount}
                    onChange={(e) => setExpenseFormData({ ...expenseFormData, amount: e.target.value })}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${editingExpense ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                    disabled={!!editingExpense}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Forma de Pagamento *</label>
                  <select
                    value={expenseFormData.payment_method}
                    onChange={(e) => setExpenseFormData({ 
                      ...expenseFormData, 
                      payment_method: e.target.value,
                      installments_count: e.target.value === 'SINGLE' ? 1 : expenseFormData.installments_count
                    })}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${editingExpense ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                    disabled={!!editingExpense}
                    required
                  >
                    <option value="SINGLE">À vista</option>
                    <option value="INSTALLMENTS">Parcelado</option>
                  </select>
                </div>
                {expenseFormData.payment_method === 'INSTALLMENTS' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Número de Parcelas *</label>
                    <input
                      type="number"
                      min="2"
                      max="24"
                      value={expenseFormData.installments_count}
                      onChange={(e) => setExpenseFormData({ ...expenseFormData, installments_count: e.target.value })}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${editingExpense ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                      disabled={!!editingExpense}
                      required
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mês Inicial *</label>
                  <select
                    value={expenseFormData.start_month}
                    onChange={(e) => setExpenseFormData({ ...expenseFormData, start_month: e.target.value })}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${editingExpense ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                    disabled={!!editingExpense}
                    required
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                      <option key={month} value={month}>
                        {new Date(2000, month - 1).toLocaleDateString('pt-BR', { month: 'long' })}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ano Inicial *</label>
                  <input
                    type="number"
                    min="2020"
                    max="2100"
                    value={expenseFormData.start_year}
                    onChange={(e) => setExpenseFormData({ ...expenseFormData, start_year: e.target.value })}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${editingExpense ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                    disabled={!!editingExpense}
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                <textarea
                  value={expenseFormData.description}
                  onChange={(e) => setExpenseFormData({ ...expenseFormData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows="3"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                <textarea
                  value={expenseFormData.notes}
                  onChange={(e) => setExpenseFormData({ ...expenseFormData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows="3"
                />
              </div>
              {expenseFormData.payment_method === 'INSTALLMENTS' && expenseFormData.amount && (
                <div className="bg-blue-50 p-4 rounded-md">
                  <p className="text-sm text-blue-800">
                    <strong>Valor por parcela:</strong> {formatCurrency(parseFloat(expenseFormData.amount) / parseInt(expenseFormData.installments_count))}
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsExpenseModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  Salvar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Visualização de Detalhes da Despesa */}
      {viewingExpenseId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Detalhes do Vale</h2>
              <button
                onClick={() => setViewingExpenseId(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {expenseDetails ? (
              <div className="p-6 space-y-6">
                {/* Informações Gerais */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Funcionário</label>
                    <p className="text-sm text-gray-900">{expenseDetails.user_name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Despesa</label>
                    <p className="text-sm text-gray-900">{expenseDetails.expense_type_name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Valor Total</label>
                    <p className="text-sm font-semibold text-gray-900">{formatCurrency(expenseDetails.amount)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Forma de Pagamento</label>
                    <p className="text-sm text-gray-900">
                      {expenseDetails.payment_method === 'SINGLE' ? 'À vista' : 'Parcelado'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    {getStatusBadge(expenseDetails.status)}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Parcelas</label>
                    <p className="text-sm text-gray-900">
                      {expenseDetails.installments_paid}/{expenseDetails.installments_count}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mês/Ano Inicial</label>
                    <p className="text-sm text-gray-900">
                      {new Date(2000, expenseDetails.start_month - 1).toLocaleDateString('pt-BR', { month: 'long' })}/{expenseDetails.start_year}
                    </p>
                  </div>
                  {expenseDetails.created_by_name && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Criado por</label>
                      <p className="text-sm text-gray-900">{expenseDetails.created_by_name}</p>
                    </div>
                  )}
                </div>

                {/* Descrição */}
                {expenseDetails.description && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{expenseDetails.description}</p>
                  </div>
                )}

                {/* Observações */}
                {expenseDetails.notes && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{expenseDetails.notes}</p>
                  </div>
                )}

                {/* Parcelas */}
                {expenseDetails.installments && expenseDetails.installments.length > 0 && (
                  <div>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                      <label className="block text-sm font-medium text-gray-700">Parcelas</label>
                      {expenseDetails.status !== 'PAID' && expenseDetails.status !== 'CANCELLED' && (
                        <button
                          type="button"
                          onClick={() => openSettleModal(expenseDetails)}
                          className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 shadow-sm transition-all self-start sm:self-auto"
                        >
                          <Zap className="w-3.5 h-3.5" />
                          <span>Quitação Manual / Forçar Abatimento</span>
                        </button>
                      )}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parcela</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vencimento</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ações</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {expenseDetails.installments.map((installment) => {
                            const monthNames = [
                              'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                              'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
                            ];
                            const dueDate = `${monthNames[installment.due_month - 1]}/${installment.due_year}`;

                            return (
                              <tr key={installment.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                  {installment.installment_number}/{expenseDetails.installments_count}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {formatCurrency(installment.amount)}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                  {dueDate}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  {installment.status === 'PAID' ? (
                                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 flex items-center gap-1 w-fit">
                                      <CheckCircle className="w-3 h-3" />
                                      Pago
                                    </span>
                                  ) : (
                                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 flex items-center gap-1 w-fit">
                                      <AlertCircle className="w-3 h-3" />
                                      Pendente
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm">
                                  {installment.status !== 'PAID' && (
                                    <button
                                      onClick={() => {
                                        setConfirmMarkPaid({
                                          show: true,
                                          expenseId: expenseDetails.id,
                                          installmentId: installment.id,
                                          installmentNumber: installment.installment_number,
                                          totalInstallments: expenseDetails.installments_count
                                        });
                                      }}
                                      className="text-green-600 hover:text-green-900 flex items-center gap-1"
                                    >
                                      <CheckCircle className="w-4 h-4" />
                                      Marcar como pago
                                    </button>
                                  )}
                                  {installment.paid_at && (
                                    <p className="text-xs text-gray-500">
                                      Pago em: {new Date(installment.paid_at).toLocaleDateString('pt-BR')}
                                    </p>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        {(() => {
                          const installmentsSum = (expenseDetails.installments || []).reduce((acc, inst) => acc + parseFloat(inst.amount || 0), 0);
                          const totalExpenseAmount = parseFloat(expenseDetails.amount || 0);
                          const diff = parseFloat((totalExpenseAmount - installmentsSum).toFixed(2));
                          const hasDiscrepancy = Math.abs(diff) >= 0.01;

                          return (
                            <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                              <tr>
                                <td className="px-4 py-3 text-xs font-bold text-gray-700 uppercase">
                                  Total Parcelas
                                </td>
                                <td className="px-4 py-3 text-sm font-bold text-gray-900">
                                  {formatCurrency(installmentsSum)}
                                </td>
                                <td colSpan="3" className="px-4 py-3 text-xs">
                                  {hasDiscrepancy ? (
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-100 text-amber-900 font-semibold border border-amber-300">
                                      <AlertCircle className="w-3.5 h-3.5 text-amber-700" />
                                      Diferença de {formatCurrency(Math.abs(diff))} para o total do vale ({formatCurrency(totalExpenseAmount)})
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-800 font-semibold border border-emerald-200">
                                      <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                                      Soma 100% exata com o valor total ({formatCurrency(totalExpenseAmount)})
                                    </span>
                                  )}
                                </td>
                              </tr>
                            </tfoot>
                          );
                        })()}
                      </table>
                    </div>
                  </div>
                )}

                {/* Botões de ação */}
                <div className="flex flex-wrap items-center justify-end gap-3 pt-4 border-t border-gray-200">
                  {expenseDetails.status === 'PENDING_APPROVAL' &&
                   currentUser?.role === 'GERENCIA' &&
                   !expenseDetails.approvals_list?.some(a => a.id === currentUser.id) && (
                    <button
                      type="button"
                      onClick={() => {
                        approveExpenseMutation.mutate(expenseDetails.id);
                        setViewingExpenseId(null);
                      }}
                      disabled={approveExpenseMutation.isPending}
                      className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm font-semibold flex items-center gap-1.5 transition-colors shadow-sm"
                    >
                      <Check className="h-4 w-4" />
                      <span>{approveExpenseMutation.isPending ? 'Aprovando...' : 'Aprovar Vale'}</span>
                    </button>
                  )}
                  {expenseDetails.payment_method === 'INSTALLMENTS' && (
                    <button
                      type="button"
                      onClick={() => recalculateInstallmentsMutation.mutate(expenseDetails.id)}
                      disabled={recalculateInstallmentsMutation.isPending}
                      className="px-3.5 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-md text-sm font-medium flex items-center gap-1.5 disabled:opacity-50 transition-colors shadow-sm"
                      title="Ajusta os centavos das parcelas pendentes para que a soma bata exatamente no centavo com o total do vale"
                    >
                      <RefreshCw className={`h-4 w-4 ${recalculateInstallmentsMutation.isPending ? 'animate-spin' : ''}`} />
                      <span>{recalculateInstallmentsMutation.isPending ? 'Recalculando...' : 'Recalcular Parcelas (Valor Exato)'}</span>
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setViewingExpenseId(null);
                      setEditingExpense(expenseDetails);
                      setExpenseFormData({
                        user_id: expenseDetails.user_id.toString(),
                        expense_type_id: expenseDetails.expense_type_id.toString(),
                        description: expenseDetails.description || '',
                        amount: expenseDetails.amount.toString(),
                        payment_method: expenseDetails.payment_method,
                        installments_count: expenseDetails.installments_count.toString(),
                        start_month: expenseDetails.start_month,
                        start_year: expenseDetails.start_year,
                        notes: expenseDetails.notes || ''
                      });
                      setIsExpenseModalOpen(true);
                    }}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center gap-2"
                  >
                    <Edit className="h-4 w-4" />
                    Editar Vale
                  </button>
                  <button
                    onClick={() => setViewingExpenseId(null)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Fechar
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">Carregando detalhes...</div>
            )}
          </div>
        </div>
      )}

      <ConfirmModal
        show={confirmApprove.show}
        onConfirm={() => {
          approveExpenseMutation.mutate(confirmApprove.expenseId);
          setConfirmApprove({ show: false, expenseId: null });
        }}
        onCancel={() => setConfirmApprove({ show: false, expenseId: null })}
        title="Aprovar Despesa"
        message="Confirma a aprovação desta despesa?"
        confirmText="Aprovar"
        type="success"
      />

      <ConfirmModal
        show={confirmDeleteExpense.show}
        onConfirm={() => {
          deleteExpenseMutation.mutate(confirmDeleteExpense.expenseId);
          setConfirmDeleteExpense({ show: false, expenseId: null });
        }}
        onCancel={() => setConfirmDeleteExpense({ show: false, expenseId: null })}
        title="Remover Despesa"
        message="Tem certeza que deseja remover esta despesa?"
        confirmText="Remover"
        type="danger"
      />

      <ConfirmModal
        show={confirmDeleteType.show}
        onConfirm={() => {
          deleteTypeMutation.mutate(confirmDeleteType.typeId);
          setConfirmDeleteType({ show: false, typeId: null });
        }}
        onCancel={() => setConfirmDeleteType({ show: false, typeId: null })}
        title="Remover Tipo de Despesa"
        message="Tem certeza que deseja remover este tipo de despesa?"
        confirmText="Remover"
        type="danger"
      />

      <ConfirmModal
        show={confirmMarkPaid.show}
        onConfirm={() => {
          markInstallmentPaidMutation.mutate({
            expenseId: confirmMarkPaid.expenseId,
            installmentId: confirmMarkPaid.installmentId
          });
          setConfirmMarkPaid({ show: false, expenseId: null, installmentId: null, installmentNumber: null, totalInstallments: null });
        }}
        onCancel={() => setConfirmMarkPaid({ show: false, expenseId: null, installmentId: null, installmentNumber: null, totalInstallments: null })}
        title="Marcar Parcela como Paga"
        message={`Marcar parcela ${confirmMarkPaid.installmentNumber}/${confirmMarkPaid.totalInstallments} como paga?`}
        confirmText="Marcar como paga"
        type="success"
      />

      {/* Modal de Quitação Manual / Forçar Abatimento no Caixa */}
      {settleModalData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-xs p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full overflow-hidden border border-emerald-200 animate-scaleIn">
            <div className="bg-emerald-600 p-5 text-white flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-white/20 rounded-xl">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-base font-bold">Quitação Manual / Forçar Abatimento</h3>
                  <p className="text-xs text-emerald-100">
                    {settleModalData.expense?.user_name || 'Funcionário'} • {settleModalData.expense?.description || 'Vale'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSettleModalData(null)}
                className="text-white/80 hover:text-white p-1 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (selectedInstallmentIds.length === 0) {
                  toast.error('Selecione pelo menos uma parcela para quitar');
                  return;
                }
                settleExpenseManualMutation.mutate({
                  id: settleModalData.expense.id,
                  data: {
                    installment_ids: selectedInstallmentIds,
                    payment_method: settlePaymentMethod,
                    payment_date: settlePaymentDate,
                    register_cashbox: settleRegisterCashbox,
                    notes: settleNotes
                  }
                });
              }}
              className="p-5 space-y-4 max-h-[80vh] overflow-y-auto"
            >
              {/* Resumo do que será liquidado */}
              {(() => {
                const allInsts = settleModalData.installments || [];
                const pendingInstallments = allInsts.filter(i => i.status !== 'PAID');
                const selectedInstallments = allInsts.filter(i => selectedInstallmentIds.includes(i.id));
                const totalSelectedAmount = selectedInstallments.reduce((sum, i) => sum + parseFloat(i.amount || 0), 0);
                const isAllSelected = allInsts.length > 0 && selectedInstallmentIds.length === allInsts.length;

                return (
                  <>
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between">
                      <div>
                        <span className="text-xs font-semibold text-emerald-800 uppercase tracking-wider">
                          Total a Liquidar Nesta Ação
                        </span>
                        <p className="text-2xl font-extrabold text-emerald-950 font-mono mt-0.5">
                          {formatCurrency(totalSelectedAmount)}
                        </p>
                        <p className="text-[11px] text-emerald-700">
                          {selectedInstallmentIds.length} de {allInsts.length} parcela(s) selecionada(s)
                          {pendingInstallments.length < allInsts.length && (
                            <span className="text-emerald-900 font-medium"> ({allInsts.length - pendingInstallments.length} já quitada(s) anteriormente)</span>
                          )}
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        {pendingInstallments.length > 0 && pendingInstallments.length < allInsts.length && (
                          <button
                            type="button"
                            onClick={() => setSelectedInstallmentIds(pendingInstallments.map(i => i.id))}
                            className="px-2.5 py-1.5 bg-white border border-emerald-200 text-emerald-700 hover:bg-emerald-50 rounded-lg text-xs font-semibold shadow-2xs transition-colors"
                          >
                            Apenas Pendentes
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            if (isAllSelected) {
                              setSelectedInstallmentIds([]);
                            } else {
                              setSelectedInstallmentIds(allInsts.map(i => i.id));
                            }
                          }}
                          className="px-3 py-1.5 bg-white border border-emerald-300 text-emerald-800 hover:bg-emerald-100 rounded-lg text-xs font-bold shadow-2xs transition-colors"
                        >
                          {isAllSelected ? 'Desmarcar Todas' : 'Marcar Todas'}
                        </button>
                      </div>
                    </div>

                    {/* Lista de Parcelas com Checkboxes */}
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">
                          Selecione as parcelas para quitar ou forçar entrada no caixa:
                        </label>
                        <span className="text-[11px] text-emerald-700 font-medium bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                          Permite marcar parcelas já pagas
                        </span>
                      </div>
                      <div className="space-y-1.5 max-h-48 overflow-y-auto border border-gray-200 rounded-xl p-2 bg-gray-50/50">
                        {allInsts.map((inst) => {
                          const isPaid = inst.status === 'PAID';
                          const isChecked = selectedInstallmentIds.includes(inst.id);

                          return (
                            <label
                              key={inst.id}
                              className={`flex items-center justify-between p-2.5 rounded-lg border transition-all cursor-pointer ${
                                isChecked
                                  ? 'bg-emerald-50 border-emerald-400 shadow-2xs ring-1 ring-emerald-400'
                                  : isPaid
                                  ? 'bg-gray-50/80 border-gray-200 hover:bg-gray-100'
                                  : 'bg-white border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex items-center gap-2.5">
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedInstallmentIds(prev => [...prev, inst.id]);
                                    } else {
                                      setSelectedInstallmentIds(prev => prev.filter(id => id !== inst.id));
                                    }
                                  }}
                                  className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500 cursor-pointer"
                                />
                                <span className="text-xs font-semibold text-gray-800">
                                  Parcela {inst.installment_number}/{settleModalData.expense?.installments_count}
                                </span>
                                <span className="text-[11px] text-gray-500">
                                  ({inst.due_month}/{inst.due_year})
                                </span>
                              </div>

                              <div className="flex items-center gap-2">
                                <span className="text-xs font-bold text-gray-900 font-mono">
                                  {formatCurrency(inst.amount)}
                                </span>
                                {isPaid ? (
                                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${
                                    isChecked
                                      ? 'text-emerald-800 bg-emerald-100 border-emerald-300'
                                      : 'text-green-700 bg-green-100 border-green-200'
                                  }`}>
                                    {isChecked ? 'Forçar Caixa' : 'Já paga'}
                                  </span>
                                ) : (
                                  <span className="text-[10px] font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded border border-amber-200">
                                    Pendente
                                  </span>
                                )}
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </>
                );
              })()}

              {/* Forma de Pagamento e Data */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                    Forma de Pagamento Recebida *
                  </label>
                  <select
                    value={settlePaymentMethod}
                    onChange={(e) => setSettlePaymentMethod(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs font-semibold focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="PIX">PIX</option>
                    <option value="DINHEIRO">Dinheiro em Espécie</option>
                    <option value="TRANSFERENCIA">Transferência Bancária</option>
                    <option value="BALANCO_CAIXA">Balanço de caixa</option>
                    <option value="OUTRO">Outro / Acordo Direto</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                    Data do Pagamento *
                  </label>
                  <input
                    type="date"
                    required
                    value={settlePaymentDate}
                    onChange={(e) => setSettlePaymentDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs font-medium focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>

              {/* Opção de Injetar Saldo no Caixa */}
              <div className="p-3.5 bg-blue-50 border border-blue-200 rounded-xl space-y-1.5">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settleRegisterCashbox}
                    onChange={(e) => setSettleRegisterCashbox(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-xs font-bold text-blue-950 flex items-center gap-1.5">
                    <ArrowDownCircle className="w-4 h-4 text-blue-600" />
                    <span>Lançar Entrada no Caixa da Empresa (Aumentar Saldo)</span>
                  </span>
                </label>
                <p className="text-[11px] text-blue-800 pl-6 leading-relaxed">
                  Cria uma movimentação de <strong>ENTRADA (INCOME)</strong> no Livro Caixa, creditando o valor no saldo financeiro da empresa na data informada.
                </p>
              </div>

              {/* Observações */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                  Observações / Comprovante (Opcional)
                </label>
                <input
                  type="text"
                  value={settleNotes}
                  onChange={(e) => setSettleNotes(e.target.value)}
                  placeholder="Ex: Pago via PIX pelo funcionário em mãos, comprovante no WhatsApp..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              {/* Rodapé com Ações */}
              <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setSettleModalData(null)}
                  disabled={settleExpenseManualMutation.isPending}
                  className="px-4 py-2 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={settleExpenseManualMutation.isPending || selectedInstallmentIds.length === 0}
                  className="px-5 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-xl shadow-sm transition-all flex items-center gap-1.5"
                >
                  {settleExpenseManualMutation.isPending ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Liquidando...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-3.5 h-3.5" />
                      <span>Confirmar Quitação e Injetar no Caixa</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesManagement;
